import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronLeft, ChevronRight, Wifi, Beaker, Users, Monitor, Shield, Droplets, Leaf, Smile, Clock, TrendingUp, Cpu, Zap, Award, Link2, Star, Crown, CheckCircle2, Check, Wrench, Settings, Handshake, Mail, MessageCircle } from 'lucide-react';
import './Home.css';
import useScrollReveal, { srPresets } from '../hooks/useScrollReveal';
import imgCarousel1 from '../assets/images/Imagem21-3.jpg';
import imgCarousel2 from '../assets/images/Imagem11-2.jpg';
import imgCarousel3 from '../assets/images/Imagem12-2.jpg';
import imgCarousel4 from '../assets/images/Imagem19-2.jpg';
import imgCarousel5 from '../assets/images/Imagem14-2.jpg';
import imgCarousel6 from '../assets/images/Imagem15-2.jpg';
import imgCarousel7 from '../assets/images/Imagem16-2.jpg';
import imgCarousel8 from '../assets/images/Imagem17-2.jpg';
import imgCarousel9 from '../assets/images/Imagem18-2.jpg';
import imgCarousel10 from '../assets/images/Imagem20-2.jpg';
import logoImg from '../assets/images/logo.png';
import wepikImg from '../assets/images/wepik-export-20240327221844XfJe-1024x683.png';
import ativo4Img from '../assets/images/Ativo-4.png';
import whatsappImg from '../assets/images/WhatsApp-Image-2024-04-17-at-21.41.04.jpeg';
import missaoImg from '../assets/images/Missao-2.png';
import visaoImg from '../assets/images/visao-4-qom03fu7pul0fvw6jarhkych5y20245y2omzlo7u4w.png';
import valoresImg from '../assets/images/Valores.png';
const CAROUSEL_IMAGES = [
  imgCarousel1,
  imgCarousel2,
  imgCarousel3,
  imgCarousel4,
  imgCarousel5,
  imgCarousel6,
  imgCarousel7,
  imgCarousel8,
  imgCarousel9,
  imgCarousel10,
];

const IPOOL_LOGO_URL = logoImg;

/** Uma única curva: topo do wave-divider e base da secção laranja = mesmo path com scaleY(-1) no tech-wave--top. viewBox 1200×120 em ambos. */
const BENEFITS_WAVE_PATH =
  'M0,0 C150,50 350,0 600,50 C850,100 1050,50 1200,0 L1200,120 L0,120 Z';

/**
 * Constante de módulo: a lista é lida uma única vez, na montagem, e o carrossel
 * re-renderiza a cada 5s — recriar o array aqui dentro seria desperdício.
 *
 * Ausentes de propósito, cada um com um dono único de opacity/transform:
 * - .advantage-card, .benefit-item, .tech-feature-card, .tech-image-wrap
 *   → estado inicial em Home.css e revelação via .is-visible do IntersectionObserver.
 * - .problems-text, .problems-image, .solution-card
 *   → `animation: fadeInUp` no CSS, que também cobre o mobile (onde o hook não roda).
 * Passá-los ao ScrollReveal fazia os dois sistemas disputarem o mesmo elemento.
 */
const REVEALS = [
  // Pilares
  { selector: '.pilares-header', options: srPresets.fadeUp },
  { selector: '.pilares-closing', options: srPresets.fadeUp },

  // Vantagens
  { selector: '.advantages-section .section-title', options: srPresets.fadeUp },

  // Benefits
  { selector: '.benefits-section .section-title-dark', options: srPresets.fadeUp },

  // Technology
  { selector: '.tech-header', options: srPresets.fadeUp },

  // Soluções RTI
  { selector: '.rti-header', options: srPresets.fadeUp },
  { selector: '.sol-card--rti', options: srPresets.fadeRight },
  { selector: '.sol-middle', options: { ...srPresets.zoomIn, delay: 150 } },
  { selector: '.sol-card--completo', options: srPresets.fadeLeft },

  // Planos RTI
  { selector: '.rti-plans-heading', options: srPresets.fadeUp },
  { selector: '.plan-card--essencial', options: srPresets.fadeUp },
  { selector: '.plan-card--avancado', options: { ...srPresets.fadeUp, delay: 120 } },
  { selector: '.plan-card--premium', options: { ...srPresets.fadeUp, delay: 240 } },
  { selector: '.rti-requirements', options: srPresets.fadeUp },
  { selector: '.rti-final-cta', options: srPresets.zoomIn },

  // Depoimentos
  { selector: '.testimonials-section h2', options: srPresets.fadeUp },
  { selector: '.testimonial-card', options: srPresets.fadeUp, interval: 100 },

  // Sobre Nós
  { selector: '.about-image', options: srPresets.fadeRight },
  { selector: '.about-text', options: srPresets.fadeLeft },

  // Missão Visão Valores
  { selector: '.mvv-card', options: srPresets.fadeUp, interval: 130 },

  // Contato
  { selector: '.contact-header', options: srPresets.fadeUp },
  { selector: '.contact-method', options: srPresets.fadeUp, interval: 150 },
  { selector: '.contact-cta-wrap', options: srPresets.zoomIn },
];

const WA_ICON = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = React.useState(false);
  const handle = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button className={`copy-btn${copied ? ' copy-btn--done' : ''}`} onClick={handle} title="Copiar">
      {copied
        ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
      }
    </button>
  );
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const len = CAROUSEL_IMAGES.length;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % len);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const targets = document.querySelectorAll(
      '.advantages-section .advantage-card, .benefits-section .benefit-item, .tech-feature-card, .tech-image-wrap'
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useScrollReveal(REVEALS);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  };

  return (
    <div className="home-page">
      <Header />
      
      {/* Hero Carousel Section */}
      <section id="inicio" className="hero-carousel">
        <div className="carousel-ticker">
          <div className="carousel-ticker__track">
            {[...Array(6)].map((_, i) => (
              <span key={i} className="carousel-ticker__item" aria-hidden={i > 0}>
                A Água&nbsp;<span className="brand-ipool">iPool</span><span className="brand-care">Care</span>
                <span className="carousel-ticker__dot">·</span>
              </span>
            ))}
          </div>
        </div>
        <div className="carousel-container">
          <div className="carousel-slides">
            {CAROUSEL_IMAGES.map((img, index) => (
              <div
                key={index}
                className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url(${img})` }}
              />
            ))}
          </div>
          
          <div className="carousel-dots">
            {CAROUSEL_IMAGES.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="problems-section">
        <div className="container">
          <div className="problems-content">
            <div className="problems-text">
              <h2>SABE TODOS AQUELES PROBLEMAS QUE ADMINISTRAR UMA PISCINA TRAZ?</h2>
              <p className="problems-lead">Administrar uma piscina envolve uma série de desafios que vão além do simples cuidado com a água. É necessário um planejamento eficiente, conhecimento técnico e uma gestão atenta para garantir que a piscina permaneça um local de lazer seguro e saudável.</p>
            </div>
            <div className="problems-image">
              <img src={wepikImg} alt="Pessoa preocupada" width={1024} height={683} decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Pilares / 4 Pilares */}
      <section id="pilares" className="solution-section">
        {/* Onda decorativa no topo */}
        <svg className="pilares-wave pilares-wave--top" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,40 C180,80 360,0 540,40 C720,80 900,0 1080,40 C1260,80 1350,20 1440,40 L1440,0 L0,0 Z" fill="rgba(255,255,255,0.10)"/>
          <path d="M0,55 C200,10 400,70 600,45 C800,20 1000,65 1200,40 C1320,25 1380,50 1440,55 L1440,0 L0,0 Z" fill="rgba(255,255,255,0.06)"/>
        </svg>

        <div className="container">
          <div className="solution-section-pilares-bg">
            <div className="pilares-header">
              <span className="pilares-kicker">A BASE DO NOSSO MÉTODO</span>
              <span className="pilares-showcase-title">Os 4 Pilares</span>
            </div>
            <div className="solution-grid">
              <div className="solution-card">
                <div className="solution-icon">
                  <Wifi size={40} />
                </div>
                <h3>IoT &amp; Monitoramento</h3>
                <p>Sensores de pH, ORP e temperatura com leituras contínuas 24/7. O sistema analisa os dados e age automaticamente.</p>
              </div>
              <div className="solution-card">
                <div className="solution-icon">
                  <Beaker size={40} />
                </div>
                <h3>Automação Química</h3>
                <p>Dosagem automática do mínimo necessário de produtos químicos — sem excessos, sem desperdício, sem risco.</p>
              </div>
              <div className="solution-card">
                <div className="solution-icon">
                  <Users size={40} />
                </div>
                <h3>Sensei + Ninja</h3>
                <p>Químico responsável com supervisão e ART, junto a equipe treinada para os cuidados estéticos diários — segurança e praticidade com procedimentos técnicos padrão.</p>
              </div>
              <div className="solution-card">
                <div className="solution-icon">
                  <Monitor size={40} />
                </div>
                <h3>Visualização em tempo real</h3>
                <p>Banhistas e administradores têm acesso direto de qualquer lugar remoto, além de monitores na área da piscina.</p>
              </div>
            </div>
            <p className="pilares-closing">Quatro pilares que trabalham juntos, ininterruptamente, 24h por dia, 7 dias por semana.</p>
          </div>
        </div>

        {/* Onda decorativa na base */}
        <svg className="pilares-wave pilares-wave--bottom" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,40 C180,0 360,80 540,40 C720,0 900,80 1080,40 C1260,0 1350,60 1440,40 L1440,80 L0,80 Z" fill="rgba(255,255,255,0.10)"/>
          <path d="M0,25 C200,70 400,10 600,35 C800,60 1000,15 1200,40 C1320,55 1380,30 1440,25 L1440,80 L0,80 Z" fill="rgba(255,255,255,0.06)"/>
        </svg>
      </section>

      {/* Advantages Section */}
      <section id="vantagens" className="advantages-section">
        <div className="container">
          <h2 className="section-title">VANTAGENS PARA OS ADMINISTRADORES</h2>
          <div className="advantages-grid">
            <div className="advantage-card">
              <div className="advantage-icon">
                <Shield size={48} />
              </div>
              <h3>Prevenção</h3>
              <ul>
                <li>Alertas automáticos sobre problemas.</li>
                <li>Menor risco de exposição à Vigilância Sanitária e de passivos trabalhistas.</li>
              </ul>
            </div>
            <div className="advantage-card">
              <div className="advantage-icon">
                <Leaf size={48} />
              </div>
              <h3>Sustentabilidade</h3>
              <ul>
                <li>Menor troca da água.</li>
                <li>Redução do uso das bombas.</li>
              </ul>
            </div>
            <div className="advantage-card highlight">
              <div className="advantage-icon">
                <Smile size={48} />
              </div>
              <h3>Paz de Espírito</h3>
              <p className="highlight-sub">Só com a <span className="brand-ipool">iPool</span><span className="brand-care">Care</span>.</p>
            </div>
            <div className="advantage-card">
              <div className="advantage-icon">
                <Smile size={48} />
              </div>
              <h3>Satisfação do usuário</h3>
              <ul>
                <li>Usuário mais satisfeito.</li>
                <li>Menos reclamações.</li>
                <li>Superação das expectativas dos banhistas.</li>
              </ul>
            </div>
            <div className="advantage-card">
              <div className="advantage-icon">
                <Clock size={48} />
              </div>
              <h3>Praticidade</h3>
              <ul>
                <li>Gestão e manutenção da automação.</li>
                <li>Controle de parâmetros com garantia da qualidade da água.</li>
                <li>Relatórios automáticos e acompanhamento em tempo real.</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Wave Divider — mesma geometria que a base da secção benefícios (tech-wave--top), espelhada */}
      <div className="wave-divider">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d={BENEFITS_WAVE_PATH} fill="#FF7043" />
        </svg>
      </div>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-title-dark">Benefícios para os usuários</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">
                <Shield size={40} />
              </div>
              <p>Liberdade para todos nadarem na água sem risco de contaminações.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <Droplets size={40} />
              </div>
              <p>Sem excesso de produtos químicos. Mergulho liberado!</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <Clock size={40} />
              </div>
              <p>Maior disponibilidade da piscina. Água boa o tempo todo.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <Leaf size={40} />
              </div>
              <p>Sem o mau cheiro da cloramina.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <TrendingUp size={40} />
              </div>
              <p>Conhecimento da temperatura e da qualidade da água, antes mesmo de entrar nela.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                <Smile size={40} />
              </div>
              <p>Sem cabelos ressecados e sem pele coçando.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="tecnologia" className="technology-section">
        <svg className="tech-wave tech-wave--top" viewBox="0 0 1200 120" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="techWaveTopGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF6B6B" />
              <stop offset="100%" stopColor="#FF8E53" />
            </linearGradient>
          </defs>
          <g transform="translate(0,120) scale(1,-1)">
            <path d={BENEFITS_WAVE_PATH} fill="url(#techWaveTopGradient)" />
          </g>
        </svg>

        <div className="container">
          <div className="tech-header">
            <h2 className="tech-title">A Tecnologia <span className="brand-ipool">iPool</span><span className="brand-care">Care</span></h2>
            <p className="tech-tagline">Desenvolvida inteiramente por nós para garantir um <strong>tratamento infalível</strong> da água da sua piscina.</p>
          </div>

          <div className="tech-diagram-row">
            <div className="tech-image-wrap">
              <img src={ativo4Img} alt="Fluxo: Casa de Máquinas, nuvem e usuário — Tecnologia iPoolCare" width={1600} height={171} decoding="async" />
            </div>
          </div>

          <div className="tech-features">
            <div className="tech-feature-card">
              <div className="tech-feature-icon"><Cpu size={26} /></div>
              <div className="tech-feature-text">
                <h4>Hardware &amp; Software exclusivos</h4>
                <p>Tecnologia proprietária desenvolvida do zero para máxima performance e confiabilidade.</p>
              </div>
            </div>
            <div className="tech-feature-card">
              <div className="tech-feature-icon"><Wifi size={26} /></div>
              <div className="tech-feature-text">
                <h4>IoT – Monitoramento contínuo 24h/7</h4>
                <p>Sensores de pH, ORP e temperatura com leituras ininterruptas e ação automática.</p>
              </div>
            </div>
            <div className="tech-feature-card">
              <div className="tech-feature-icon"><Monitor size={26} /></div>
              <div className="tech-feature-text">
                <h4>Controle remoto total</h4>
                <p>Plataforma de monitoramento acessível de qualquer lugar, a qualquer hora.</p>
              </div>
            </div>
            <div className="tech-feature-card">
              <div className="tech-feature-icon"><Zap size={26} /></div>
              <div className="tech-feature-text">
                <h4>Comunicação em tempo real</h4>
                <p>Eficiência e precisão garantidas em todo o processo de tratamento da água.</p>
              </div>
            </div>
          </div>
        </div>

        <svg className="tech-wave tech-wave--bottom" viewBox="0 0 1200 120" preserveAspectRatio="none" aria-hidden="true">
          <path d={BENEFITS_WAVE_PATH} fill="url(#techWaveTopGradient)" />
        </svg>
      </section>

      {/* RTI Section — sem segunda onda em espelho; a transição laranja vem só do tech-wave--bottom */}
      <section id="nossas-solucoes" className="rti-section">
        <div className="container">
          {/* Slide 1 - Duas Soluções */}
          <div className="rti-header">
            <h2 className="rti-main-title">AS SOLUÇÕES <span className="brand-ipool">IPOOL</span><span className="rti-title-care brand-care">CARE</span></h2>
            <h3 className="rti-subtitle-main">Duas formas de cuidar da sua piscina.<br/>Um único padrão de excelência.</h3>
            <p className="rti-intro">Cada estabelecimento tem uma realidade diferente. Temos uma solução para cada uma.</p>
          </div>

          <div className="rti-two-solutions">
            {/* Card RTI — agora à esquerda com cor navy */}
            <div className="sol-card sol-card--rti">
              <div className="sol-card-header sol-card-header--navy">
                <div className="sol-card-icon"><Link2 size={38} /></div>
                <span className="sol-card-badge">INTEGRAÇÃO RTI</span>
                <h3>RTI <span className="brand-ipool">iPool</span><span className="brand-care">Care</span></h3>
                <p className="sol-card-tagline">Para quem já tem automação e quer supervisão profissional.</p>
              </div>
              <div className="sol-card-body">
                <ul className="sol-features">
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Integração com automação existente</span>
                      <span className="sol-li-tail"> — compatível com diversas marcas</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Supervisão química profissional</span>
                      <span className="sol-li-tail"> + Sensei com visitas mensais</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Monitoramento remoto</span>
                      <span className="sol-li-tail">, relatórios NBR/ABNT, link exclusivo para usuários</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Treinamento Ninja</span>
                      <span className="sol-li-tail"> + alertas antecipados de problemas</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Emissão de ART</span>
                    </span>
                  </li>
                </ul>
                <div className="sol-banner sol-banner--navy">
                  <span className="sol-banner__title">Supervisão profissional. Segurança jurídica.</span>
                </div>
              </div>
            </div>

            {/* Centro: OU + texto */}
            <div className="sol-middle">
              <span className="sol-ou">OU</span>
            </div>

            {/* Card Completo — agora à direita com cor laranja */}
            <div className="sol-card sol-card--completo">
              <div className="sol-card-header sol-card-header--orange">
                <div className="sol-card-icon"><Award size={38} /></div>
                <span className="sol-card-badge">SOLUÇÃO COMPLETA</span>
                <h3>Sistema <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> Completo</h3>
                <p className="sol-card-tagline">Para quem quer a solução total — sem preocupações.</p>
              </div>
              <div className="sol-card-body">
                <ul className="sol-features">
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Automação <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> exclusiva</span>
                      <span className="sol-li-tail"> — sensores e controladores próprios instalados</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Dosagem química com máxima precisão</span>
                      <span className="sol-li-tail"> — garantida pelo sistema proprietário</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Monitoramento remoto 24h/7</span>
                      <span className="sol-li-tail"> + Sensei + Ninjas treinados</span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Manutenção dos equipamentos <strong>incluída</strong></span>
                    </span>
                  </li>
                  <li>
                    <span className="sol-check" aria-hidden><Check size={17} strokeWidth={2.5} /></span>
                    <span className="sol-li-text">
                      <span className="sol-li-lead">Acompanhamento por químico responsável</span>
                      <span className="sol-li-tail"> + emissão de ART</span>
                    </span>
                  </li>
                </ul>
                <div className="sol-banner sol-banner--orange">
                  <span className="sol-banner__title">Qualidade garantida. Resultado previsível.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Planos RTI */}
          <div className="rti-plans">
            <div className="rti-plans-heading">
              <span className="rti-plans-kicker">ESCOLHA O MELHOR PARA VOCÊ</span>
              <h2 className="rti-plans-title">Nossos Planos RTI</h2>
              <p className="rti-plans-desc">Orçamento personalizado sob consulta. Cada estabelecimento é único.</p>
            </div>

            <div className="rti-plans-grid">
              {/* Essencial */}
              <div className="plan-card plan-card--essencial">
                <div className="plan-card-header plan-card-header--orange">
                  <div className="plan-card-icon"><Link2 size={32} /></div>
                  <h3>Essencial</h3>
                  <p className="plan-card-tagline">A solução RTI para começar</p>
                </div>
              </div>

              {/* Avançado */}
              <div className="plan-card plan-card--avancado">
                <div className="plan-card-header plan-card-header--navy">
                  <div className="plan-card-icon"><Star size={32} /></div>
                  <h3>Avançado</h3>
                  <p className="plan-card-tagline">RTI para necessidades específicas</p>
                </div>
              </div>

              {/* Premium */}
              <div className="plan-card plan-card--premium">
                <div className="plan-card-header plan-card-header--dark">
                  <div className="plan-card-icon"><Crown size={32} /></div>
                  <h3>Premium</h3>
                  <p className="plan-card-tagline">A experiência RTI máxima</p>
                </div>
              </div>
            </div>

            {/* Requisitos */}
            <div className="rti-requirements">
              <h3>Requisitos para adesão</h3>
              <div className="requirements-grid">
                <div className="requirement-col">
                  <div className="req-icon"><Wrench size={22} /></div>
                  <h4>ESTRUTURA TÉCNICA</h4>
                  <ul>
                    <li><CheckCircle2 size={14} /><span>Casa de máquinas adequada</span></li>
                    <li><CheckCircle2 size={14} /><span>Sistema de filtração funcional</span></li>
                    <li><CheckCircle2 size={14} /><span>Automação básica de dosagem</span></li>
                  </ul>
                </div>
                <div className="requirement-col">
                  <div className="req-icon"><Settings size={22} /></div>
                  <h4>CONDIÇÕES OPERACIONAIS</h4>
                  <ul>
                    <li><CheckCircle2 size={14} /><span>Reposição adequada de água</span></li>
                    <li><CheckCircle2 size={14} /><span>Manutenção preventiva</span></li>
                    <li><CheckCircle2 size={14} /><span>Colaborador responsável</span></li>
                  </ul>
                </div>
                <div className="requirement-col">
                  <div className="req-icon"><Handshake size={22} /></div>
                  <h4>COMPROMISSO</h4>
                  <ul>
                    <li><CheckCircle2 size={14} /><span>Cumprimento de protocolos</span></li>
                    <li><CheckCircle2 size={14} /><span>Permissão para auditorias</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Final */}
          <div className="rti-final-cta">
            {/* Piscina decorativa */}
            <svg className="cta-rocket" viewBox="0 0 160 130" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              {/* Borda da piscina */}
              <rect x="8" y="38" width="144" height="84" rx="12" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5"/>
              {/* Água */}
              <rect x="8" y="55" width="144" height="67" rx="0" fill="rgba(100,200,255,0.22)"/>
              {/* Ondas da água */}
              <path d="M8 70 C28 62 48 78 68 70 C88 62 108 78 128 70 C138 66 148 70 152 70 L152 80 C138 80 128 84 108 80 C88 76 68 84 48 80 C28 76 18 82 8 80 Z" fill="rgba(255,255,255,0.2)"/>
              <path d="M8 90 C30 83 50 97 70 90 C90 83 110 97 130 90 C142 86 150 90 152 90 L152 98 C140 98 130 102 110 98 C90 94 70 102 50 98 C30 94 18 100 8 98 Z" fill="rgba(255,255,255,0.13)"/>
              {/* Poste esquerdo da escada */}
              <rect x="44" y="8" width="5" height="82" rx="2.5" fill="rgba(255,255,255,0.6)" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
              {/* Poste direito da escada */}
              <rect x="68" y="8" width="5" height="82" rx="2.5" fill="rgba(255,255,255,0.6)" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
              {/* Degraus da escada */}
              <rect x="44" y="22" width="29" height="5" rx="2.5" fill="rgba(255,255,255,0.85)"/>
              <rect x="44" y="38" width="29" height="5" rx="2.5" fill="rgba(255,255,255,0.85)"/>
              <rect x="44" y="54" width="29" height="5" rx="2.5" fill="rgba(255,255,255,0.85)"/>
              <rect x="44" y="70" width="29" height="5" rx="2.5" fill="rgba(255,255,255,0.85)"/>
              {/* Corrimão curvo no topo */}
              <path d="M30 10 C30 6 38 4 46.5 4 C55 4 70 4 72.5 4 C80 4 88 6 88 10" stroke="rgba(255,255,255,0.7)" strokeWidth="4.5" strokeLinecap="round" fill="none"/>
              {/* Bolhas */}
              <circle cx="100" cy="100" r="4" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2"/>
              <circle cx="115" cy="88" r="2.5" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
              <circle cx="126" cy="105" r="3" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
            </svg>

            <h3>Pronto para escolher a melhor solução?</h3>
            <a href="https://api.whatsapp.com/send?phone=5511982922298" className="rti-cta-button" target="_blank" rel="noopener noreferrer">
              Fale Conosco
            </a>
          </div>
        </div>
        <svg className="rti-wave rti-wave--bottom" viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0,55 C180,15 360,85 540,50 C720,15 900,75 1080,45 C1260,15 1380,65 1440,50 L1440,100 L0,100 Z" fill="rgba(102,126,234,0.10)"/>
          <path d="M0,70 C200,35 420,90 640,60 C860,30 1060,80 1260,55 C1360,42 1410,68 1440,62 L1440,100 L0,100 Z" fill="rgba(118,75,162,0.07)"/>
          <path d="M0,83 C280,55 520,100 760,78 C1000,56 1220,96 1440,80 L1440,100 L0,100 Z" fill="#ffffff"/>
        </svg>
      </section>

      {/* Testimonials Section */}
      <section id="depoimentos" className="testimonials-section">
        <div className="container">
          <h2>Depoimentos de nossos clientes</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <p>"Agora eu entro na água para dar aula e sinto uma grande diferença em relação às outras piscinas onde sou professor. É muito bom poder ver como está a água aqui na TV."</p>
              <div className="testimonial-author">Prof. de natação do Clube do Plaza Mayor Ipiranga</div>
            </div>
            <div className="testimonial-card">
              <p>"Todos estão comentando como está linda a piscina coberta. Desde que a <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> esta aqui, nunca mais tivemos problemas."</p>
              <div className="testimonial-author">Ninja da piscina do Clube Itahye</div>
            </div>
            <div className="testimonial-card">
              <p>Eu nunca mais me preocupei com a piscina e ainda a <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> me avisa antes de acontecer um problema... Foi assim com o aquecedor da interna e com a bomba da piscina grande.</p>
              <div className="testimonial-author">Gestor do Boulevard Tamboré</div>
            </div>
            <div className="testimonial-card">
              <p>"Antes tinha um cheiro estranho na piscina, eu não deixava o meu filho ficar muito tempo, senão ele ficava com coceira e cheiro de cloro. Agora está ótima, estamos muito satisfeitos!"</p>
              <div className="testimonial-author">Morador do Clima Mascote</div>
            </div>
            <div className="testimonial-card">
              <p>"Depois da <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> reduzimos o consumo de água e de energia e zeramos as reclamações dos moradores em relação à qualidade da água."</p>
              <div className="testimonial-author">Gestor do Praça das Águas</div>
            </div>
            <div className="testimonial-card">
              <p>"Antes chegamos a usar três empresas ao mesmo tempo para tratar a nossa piscina, que é a nossa "cereja do bolo"... A <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> sozinha resolveu todas as nossas ofensivas em relação a piscina, agora ela é um orgulho nosso."</p>
              <div className="testimonial-author">Diretor do Hilton Morumbi</div>
            </div>
          </div>
        </div>

        <svg className="testimonials-wave" viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0,55 C180,15 360,85 540,50 C720,15 900,75 1080,45 C1260,15 1380,65 1440,50 L1440,100 L0,100 Z" fill="rgba(27,48,96,0.07)"/>
          <path d="M0,70 C200,35 420,90 640,60 C860,30 1060,80 1260,55 C1360,42 1410,68 1440,62 L1440,100 L0,100 Z" fill="rgba(27,48,96,0.06)"/>
          <path d="M0,83 C280,55 520,100 760,78 C1000,56 1220,96 1440,80 L1440,100 L0,100 Z" fill="#ffffff"/>
        </svg>
      </section>

      {/* About Us Section */}
      <section id="nos" className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-image">
              <img src={whatsappImg} alt="Equipe iPoolCare" width={960} height={1280} decoding="async" />
            </div>
            <div className="about-text">
              <h2>Nós</h2>
              <h3>Conheça a <span className="brand-ipool">iPool</span><span className="brand-care">Care</span>: uma inovação em tratamento de águas de piscinas</h3>
              <p>A <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> emergiu em 2019 como uma inovação destinada a repensar o tratamento de piscinas. Com o poder da tecnologia IoT, nossa plataforma mergulha na água e oferece uma gestão de piscinas inteligente, intuitiva e livre de preocupações para todos os amantes da água.</p>
              <p>Esqueça o ditado "O que os olhos não veem, o coração não sente". Anos de experiência nos mostraram que há muito mais sob a superfície da água do que as pessoas imaginam e acreditamos no poder dos dados para manter cada piscina sob nosso cuidado límpida e saudável. Meticulosa observação e pesquisa nos ensinaram que equilibrar a química da piscina é essencial, mas não precisa ser uma tarefa complicada e com desperdícios.</p>
              <p>Nossa tecnologia IoT atua como os olhos e o cérebro da piscina, monitorando continuamente a qualidade da água e ajustando-se automaticamente para manter tudo em harmonia. Fornecemos informações detalhadas e acessíveis para os administradores e usuários, garantindo que todos os banhistas desfrutem de uma água impecável a qualquer hora.</p>
              <p>A <span className="brand-ipool">iPool</span><span className="brand-care">Care</span> está aqui para elevar o tratamento de piscinas a um novo nível de clareza e facilidade.</p>
              <h4>Pronto para um mergulho na era da tecnologia inteligente?</h4>
            </div>
          </div>
        </div>
        <svg className="nos-wave" viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0,60 C180,20 360,90 540,55 C720,20 900,80 1080,50 C1260,20 1380,70 1440,55 L1440,100 L0,100 Z" fill="rgba(27,48,96,0.08)"/>
          <path d="M0,75 C200,40 400,95 600,65 C800,35 1000,85 1200,60 C1320,45 1390,72 1440,68 L1440,100 L0,100 Z" fill="rgba(27,48,96,0.06)"/>
          <path d="M0,88 C240,65 480,100 720,80 C960,60 1200,95 1440,82 L1440,100 L0,100 Z" fill="#f0f4f9"/>
        </svg>
      </section>

      {/* Mission Vision Values Section */}
      <section id="missao" className="mvv-section">
        <div className="container">
          <div className="mvv-section-header">
            <span className="mvv-kicker">NOSSA ESSÊNCIA</span>
            <h2 className="mvv-title">Missão, Visão e Valores</h2>
          </div>
          <div className="mvv-grid">
            <div className="mvv-card">
              <div className="mvv-icon">
                <img src={missaoImg} alt="Missão" width={1600} height={1100} decoding="async" />
              </div>
              <h3>Missão</h3>
              <ul>
                <li>Ser o maior e melhor sistema de controle e monitoramento de qualidade de águas do mercado.</li>
              </ul>
            </div>
            <div className="mvv-card">
              <div className="mvv-icon">
                <img src={visaoImg} alt="Visão" width={706} height={712} decoding="async" />
              </div>
              <h3>Visão</h3>
              <ul>
                <li>Entregar aos usuários a certeza de uma água segura, aos administradores a paz de espírito em saber sobre os aspectos da água o tempo todo, aos parceiros um método infalível de tratamento de águas e ao meio ambiente sustentabilidade;</li>
                <li>Agregar valor para o cliente e para empresa;</li>
                <li>Transformar a cultura de percepção da qualidade da água.</li>
              </ul>
            </div>
            <div className="mvv-card">
              <div className="mvv-icon">
                <img src={valoresImg} alt="Valores" width={1600} height={1046} decoding="async" />
              </div>
              <h3>Valores</h3>
              <ul>
                <li>Promessa feita, promessa cumprida;</li>
                <li>Otimismo;</li>
                <li>Transparência e confiança;</li>
                <li>Aprendizado, melhoria e crescimento contínuos (bola de neve positiva!);</li>
                <li>Relações ganha-ganha;</li>
                <li>Respeito à vida, às pessoas, ao meio ambiente.</li>
              </ul>
            </div>
          </div>
        </div>
        <svg className="section-wave" viewBox="0 0 1440 70" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0,20 C360,70 720,0 1080,40 C1260,58 1360,25 1440,20 L1440,70 L0,70 Z" fill="#1b3060"/>
        </svg>
      </section>

      {/* Contact Section */}
      <section id="contato" className="contact-section">
        <div className="container">

          <div className="contact-header">
            <span className="contact-kicker">CONTATO</span>
            <h2 className="contact-title">Fale Conosco</h2>
            <div className="contact-desc">
              <p>Estamos aqui para ajudar e responder a todas as suas perguntas! Se você precisar de assistência, informação adicional ou apenas quiser dizer &quot;oi&quot;, não hesite em entrar em contato conosco.</p>
              <p>Ficaremos felizes em ouvir de você e responderemos o mais rápido possível. Aguardamos ansiosamente sua mensagem!</p>
            </div>
          </div>

          <div className="contact-grid">
            <div className="contact-method">
              <div className="contact-method-icon"><Mail size={26} /></div>
              <h3>Email</h3>
              <p>Prefere contatar-nos por e-mail? Estamos à disposição! Basta enviar uma mensagem para qualquer um dos endereços abaixo, e teremos o prazer de ajudar em tudo o que precisar. Por favor, note que nosso prazo de resposta por e-mail pode ser de até 2 dias úteis.</p>
              <div className="contact-details">
                <div className="contact-item">
                  <a href="mailto:fabiola@ipoolcare.com"><Mail size={14} />fabiola@ipoolcare.com</a>
                  <CopyButton value="fabiola@ipoolcare.com" />
                </div>
                <div className="contact-item">
                  <a href="mailto:mauro@ipoolcare.com"><Mail size={14} />mauro@ipoolcare.com</a>
                  <CopyButton value="mauro@ipoolcare.com" />
                </div>
              </div>
            </div>

            <div className="contact-method contact-method--whatsapp">
              <div className="contact-method-icon">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3>WhatsApp</h3>
              <p>Prefere entrar em contato por WhatsApp? Sem problemas! Basta clicar no link abaixo e nos enviar uma mensagem, estamos totalmente à disposição para ajudar.</p>
              <div className="contact-details">
                <div className="contact-item">
                  <a href="https://api.whatsapp.com/send?phone=5511982922298" target="_blank" rel="noopener noreferrer">{WA_ICON}(11) 98292-2298</a>
                  <CopyButton value="(11) 98292-2298" />
                </div>
                <div className="contact-item">
                  <a href="https://api.whatsapp.com/send?phone=5511982770880" target="_blank" rel="noopener noreferrer">{WA_ICON}(11) 98277-0880</a>
                  <CopyButton value="(11) 98277-0880" />
                </div>
              </div>
            </div>
          </div>

          <div className="contact-cta-wrap">
            <a href="https://api.whatsapp.com/send?phone=5511982922298" className="contact-main-cta" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Iniciar conversa no WhatsApp
            </a>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
